export type DisabledBySnapshot = {
  deletedAt?: string | null;
  disabledByUserId?: string | null;
  disabledByName?: string | null;
  disabledByEmail?: string | null;
};

export function parseDisabledBySnapshot(
  raw: Record<string, unknown>,
): DisabledBySnapshot {
  return {
    deletedAt:
      raw.deletedAt != null
        ? String(raw.deletedAt)
        : raw.deleted_at != null
          ? String(raw.deleted_at)
          : null,
    disabledByUserId:
      raw.disabledByUserId != null
        ? String(raw.disabledByUserId)
        : raw.disabled_by_user_id != null
          ? String(raw.disabled_by_user_id)
          : null,
    disabledByName:
      raw.disabledByName != null
        ? String(raw.disabledByName)
        : raw.disabled_by_name != null
          ? String(raw.disabled_by_name)
          : null,
    disabledByEmail:
      raw.disabledByEmail != null
        ? String(raw.disabledByEmail)
        : raw.disabled_by_email != null
          ? String(raw.disabled_by_email)
          : null,
  };
}

export function formatDisabledTimestamp(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDisabledByLabel(snapshot: DisabledBySnapshot): string | null {
  const name = snapshot.disabledByName?.trim();
  const email = snapshot.disabledByEmail?.trim();
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} (${email})`;
  }
  return name || email || snapshot.disabledByUserId || null;
}

export function formatDisabledStatusText(snapshot: DisabledBySnapshot): string | null {
  const when = formatDisabledTimestamp(snapshot.deletedAt);
  if (!when) return null;
  const by = formatDisabledByLabel(snapshot);
  return by ? `Disabled ${when} by ${by}` : `Disabled ${when}`;
}

export type DisabledEntityKind = "user" | "organization";

export function buildDisabledStatusDescription(
  kind: DisabledEntityKind,
  options?: { canRestore?: boolean },
): string {
  const canRestore = options?.canRestore ?? false;

  if (kind === "user") {
    const tail = canRestore
      ? "Restore the account to allow access again."
      : "Contact an administrator to restore access.";
    return `This user is disabled and cannot sign in. ${tail}`;
  }

  const tail = canRestore
    ? "Restore the organization to return it to full activity."
    : "Contact a super-admin to restore it.";
  return `This organization is disabled. Members cannot see or interact with it. ${tail}`;
}

export function getDisabledStatusTitle(kind: DisabledEntityKind): string {
  return kind === "user" ? "Account disabled" : "Organization disabled";
}
